/**
 * Redux Store Configuration
 *
 * configureStore wraps Redux's createStore with sensible defaults:
 * - Automatically adds redux-thunk middleware (for async actions)
 * - Enables Redux DevTools in development
 * - Adds serializableCheck middleware (warns if non-serializable values
 *   like functions or class instances appear in state — our state is
 *   all primitives/arrays/plain objects, so this is safe)
 *
 * The typed hooks (useAppSelector, useAppDispatch) use RTK 2.x's
 * `withTypes()` API — cleaner than the older TypedUseSelectorHook pattern.
 * These hooks are the ONLY way to access the store from components.
 */

import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import dashboardReducer from "./dashboardSlice";

export const store = configureStore({
  reducer: {
    dashboard: dashboardReducer,
  },
});

/**
 * RootState: inferred from store.getState() return type.
 * As slices are added, this type automatically includes them.
 * Usage: useAppSelector((state: RootState) => state.dashboard.events)
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * AppDispatch: inferred from store.dispatch type.
 * Includes thunk dispatch signature if async thunks are used.
 */
export type AppDispatch = typeof store.dispatch;

/**
 * Typed hooks — use these throughout the app instead of plain
 * useDispatch/useSelector to get automatic TypeScript inference.
 *
 * RTK 2.x pattern: useSelector.withTypes<RootState>() returns a
 * pre-typed hook, eliminating the need for a separate type assertion
 * at every call site. This is the officially recommended approach.
 */
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
